/**
 * Copyright (c) HashiCorp, Inc.
 * SPDX-License-Identifier: MPL-2.0
 */

import React, { PropsWithChildren, useState, useEffect } from 'react';
import { Typography, makeStyles } from '@material-ui/core';
import ExtensionIcon from '@material-ui/icons/Extension';
import CategoryIcon from '@mui/icons-material/Category';
import CloudCircleIcon from '@mui/icons-material/CloudCircle';
import AllInclusiveIcon from '@mui/icons-material/AllInclusive';
import LibraryBooks from '@material-ui/icons/LibraryBooks';
import CreateComponentIcon from '@material-ui/icons/AddCircleOutline';
import GroupIcon from '@mui/icons-material/Group';
import GitHubIcon from '@mui/icons-material/GitHub';
import LogoFull from './LogoFull';
import LogoIcon from './LogoIcon';
import {
  Settings as SidebarSettings,
  UserSettingsSignInAvatar,
} from '@backstage/plugin-user-settings';
import { SidebarSearchModal } from '@backstage/plugin-search';
import {
  Sidebar,
  sidebarConfig,
  SidebarDivider,
  SidebarGroup,
  SidebarItem,
  SidebarPage,
  useSidebarOpenState,
  Link,
  SidebarExpandButton,
} from '@backstage/core-components';
import SearchIcon from '@material-ui/icons/Search';
import { SubMenuItem } from './SubMenuItem';
import logo from './Logos/android-chrome-192x192.png'
import { discoveryApiRef, identityApiRef, useApi } from '@backstage/core-plugin-api';
import HomeOutlinedIcon from '@mui/icons-material/HomeOutlined';
import SavingsOutlinedIcon from '@mui/icons-material/SavingsOutlined';
import MoneyIcon from '@material-ui/icons/MonetizationOn';

const useSidebarLogoStyles = makeStyles({
  root: {
    width: sidebarConfig.drawerWidthClosed,
    height: 3 * sidebarConfig.logoHeight,
    display: 'flex',
    flexFlow: 'row nowrap',
    alignItems: 'center',
    marginBottom: -14,
  },
  link: {
    display: 'flex',
    alignItems: 'center',
    marginLeft: '24px',
    gap: '5px'
  },
  logoHeader: {
    fontSize: '20px',
    fontWeight: 'bold',
    color: '#94fce4',
    width: 'max-content'
  },
  image: {
    height: '30px'
  }
});


const SidebarLogoNew = () => {
  const classes = useSidebarLogoStyles();
  const { isOpen } = useSidebarOpenState();

  return (
    <div className={classes.root}>
      <Link to="/" underline="none" aria-label="Home" className={classes.link}>
        <img src={logo} alt='Logo' className={classes.image} />
        {isOpen && (
          <Typography className={classes.logoHeader}>
             CLOUDOPS
          </Typography>
        )}
      </Link>
    </div>
  )
}

export const Root = ({ children }: PropsWithChildren<{}>) => {

  const identityApi = useApi(identityApiRef)
  const discoveryApi = useApi(discoveryApiRef)
  const [isAdmin, setIsAdmin] = useState(false)

  useEffect(() => {
    const fetchAdmin = async () => {
      try {
        const baseurl = await discoveryApi.getBaseUrl('user')
        const bearertoken = await identityApi.getCredentials();
        const adminResponse = await fetch(`${baseurl}/admin`, {
          headers: {
            "Authorization": `Bearer ${bearertoken.token}`
          }
        }
        )
        const admin = await adminResponse.json();
        const isAdmin = admin.isAdmin
        setIsAdmin(isAdmin)
      }
      catch (error) {
        console.log(error, 'error')
      }
    }
    fetchAdmin();
  }, [])

  sidebarConfig.drawerWidthOpen = 300;
  return (

    <SidebarPage>
      <Sidebar sidebarOptions={{ drawerWidthOpen: 300 }} >
        <SidebarLogoNew />
        <SidebarGroup label="Search" icon={<SearchIcon />} to="/search">
          <SidebarSearchModal />
        </SidebarGroup>
        <SidebarDivider />
        <SidebarItem icon={HomeOutlinedIcon} to="/home" text="Home" />

        <SidebarGroup label="Infrastructure Management" >
          <SubMenuItem icon={CategoryIcon} title='Infrastructure Management' depth={0} dropdownItems={[
            // {
            //   title:'Terraform',
            //   to:'/catalog',
            //   depth:1
            // },
            {
              title: 'Terraform',
              to: '/terraform',
              depth: 1
            }
          ]} />
        </SidebarGroup>
        <SidebarGroup label="CI/CD" >
          <SubMenuItem icon={AllInclusiveIcon} title='CI/CD' depth={0} dropdownItems={[
            {
              title: 'Jenkins',
              to: '/jenkins-jobs',
              depth: 1
            }
          ]} />
        </SidebarGroup>
        <SidebarGroup label="Version Control" >
          <SubMenuItem icon={GitHubIcon} title='Version Control' depth={0} dropdownItems={[
            {
              title: 'Github',
              to: '/repositories',
              depth: 1,
              children: [
                {
                  title: 'Heat Map',
                  to: '/heatmap',
                  depth: 2
                },
              ]
            }
          ]} />
        </SidebarGroup>
        <SidebarGroup label="Cloud Service" icon={CloudCircleIcon}  >
          <SubMenuItem title='Cloud Services' icon={CloudCircleIcon} depth={0} dropdownItems={[
            {
              title: 'AWS',
              to: '/aws',
              depth: 1,
              children: [
                {
                  title: 'Resource Cost',
                  to: '/resourcecost',
                  depth: 2
                },
                {
                  title: 'Cost Optimization Opportunities',
                  to: '/cost-summary',
                  depth: 2,
                  children: [
                    {
                      title: 'Cost Optimization Hub',
                      to: '/cost-optimization-hub',
                      depth: 3
                    },
                    {
                      title: 'EC2 Savings Plans',
                      to: '/ec2',
                      depth: 3
                    },
                    {
                      title: 'S3 Buckets',
                      to: '/s3-buckets',
                      depth: 3
                    },
                    {
                      title: 'Network Interfaces',
                      to: '/network-interfaces',
                      depth: 3
                    },
                    {
                      title: 'Unused Volumes',
                      to: '/unused-volumes',
                      depth: 3
                    },
                    {
                      title: 'EC2 Snapshots',
                      to: '/ec2snapshots',
                      depth: 3
                    },
                    {
                      title: 'RDS',
                      to: '/rds',
                      depth: 3
                    },
                    {
                      title: 'RDS Snapshot',
                      to: '/rds-snapshot',
                      depth: 3
                    },
                    {
                      title: 'Virtual Private Cloud',
                      to: '/vpc',
                      depth: 3
                    },
                    {
                      title: 'Key Management Service',
                      to: '/kms-cost',
                      depth: 3
                    }
                  ]
                }
              ]
            },
            // {
            //   title:'GCP',
            //   to:'/catalog',
            //    depth:1
            // }
          ]}
          />
        </SidebarGroup>
        {/* <SidebarGroup label="OpenCost" >
          <SidebarItem icon={MoneyIcon} to="opencost" text="OpenCost" />
        </SidebarGroup> */}
        <SidebarGroup label="APIs" >
          <SidebarItem icon={ExtensionIcon} to="api-docs" text="APIs" />
        </SidebarGroup>

        {/* <SidebarGroup label="Docs" >
          <SidebarItem icon={LibraryBooks} to="docs" text="Docs" />
        </SidebarGroup> */}
        {/* <SidebarGroup label="Create..." >
          <SidebarItem icon={CreateComponentIcon} to="create" text="Create..." />
        </SidebarGroup> */}
        {/* End global nav */}
        <SidebarDivider />
        {/* <SidebarScrollWrapper>
          <SidebarItem icon={MapIcon} to="tech-radar" text="Tech Radar" />
        </SidebarScrollWrapper>
      <SidebarSpace /> */}
        {/* <SidebarDivider /> */}
        {isAdmin && (
          <SidebarGroup label="User Management">
            <SidebarItem icon={GroupIcon} to="user-management" text='User Management' />
          </SidebarGroup>
        )}
        {/* {isAdmin && (
         <SidebarGroup label="Custom Settings">
           <SidebarItem icon={ConstructionIcon} to="custom-settings" text='Configurations' />
         </SidebarGroup>
       )
       } */}
        <SidebarGroup
          label="Settings"
          icon={<UserSettingsSignInAvatar />}
          to="/settings"
        >
          <SidebarSettings />
        </SidebarGroup>
        <SidebarExpandButton />
      </Sidebar>
      {children}
    </SidebarPage>

  );
}
